export const portfolio = [
  {
    id: 1,
    title: 'Nepean Specialist Clinic',
    client: 'Nepean Specialist Clinic',
    service: 'Web Design',
    industry: 'Healthcare',
    year: 2024,
    description: 'A professional healthcare website for a specialist medical clinic, focused on patient trust and easy appointment access.',
    longDescription: 'Designed and developed a clean, authoritative website for Nepean Specialist Clinic — a multi-specialty medical practice in Australia. The site communicates professionalism and patient-centric care, with clear service pages, doctor profiles, and a frictionless contact flow.',
    challenge: 'The clinic needed a digital presence that inspired confidence in prospective patients while remaining accessible and easy to navigate for all age groups. Their previous site was outdated, slow, and failed to reflect the calibre of care they provide.',
    approach: 'We built a modern WordPress site with a calm colour palette and structured layout that emphasises clarity. Service pages were written for both patients and referring GPs, doctor bios were crafted to humanise the team, and performance optimisation ensured fast load times on all devices.',
    impact: {
      metrics: [
        { label: 'Page Load Speed', value: '<2s' },
        { label: 'Mobile Friendly', value: '100%' },
        { label: 'Services Showcased', value: '10+' },
        { label: 'Doctor Profiles', value: '5+' }
      ]
    },
    outcomes: [
      'Established a credible online presence for a multi-specialist clinic',
      'Improved patient discovery through clear service and doctor pages',
      'Reduced phone enquiries by surfacing answers to common questions online',
      'Fully responsive design catering to patients of all demographics'
    ],
    duration: '6 weeks',
    technologies: ['WordPress', 'Elementor', 'Custom CSS', 'WPForms'],
    image: require('@/assets/case studies/nepean.png'),
    link: 'https://nepeanspecialistclinic.com.au/',
    featured: true
  },
  {
    id: 2,
    title: 'Cabinaway',
    client: 'Cabinaway',
    service: 'Web Design',
    industry: 'Hospitality',
    year: 2024,
    description: 'A visually rich website for a cabin rental and glamping experience platform, designed to inspire and convert.',
    longDescription: 'Cabinaway connects travellers with unique cabin stays and glamping experiences. We crafted an immersive website that leads with stunning imagery and concise copy, guiding visitors from inspiration to booking with minimal friction.',
    challenge: 'Cabinaway needed a site that could compete with established booking platforms on visual impact while maintaining the intimacy of a boutique brand. Converting casual browsers into direct bookings — without relying on third-party platforms — was the key goal.',
    approach: 'We prioritised large hero imagery, intuitive navigation, and a clean listing layout. The booking flow was simplified, and trust signals (reviews, features, FAQs) were woven throughout to reduce drop-off at each stage of the funnel.',
    impact: {
      metrics: [
        { label: 'Direct Booking Focus', value: '100%' },
        { label: 'Visual-First Design', value: 'Yes' },
        { label: 'Mobile Optimised', value: 'Yes' },
        { label: 'Load Time', value: '<2.5s' }
      ]
    },
    outcomes: [
      'Created an immersive brand experience that differentiates from OTA platforms',
      'Streamlined the path from discovery to direct booking',
      'Showcased property listings with rich photography and clear details',
      'Built a site that grows with the business as new cabins are added'
    ],
    duration: '5 weeks',
    technologies: ['WordPress', 'Elementor', 'WooCommerce', 'Custom CSS'],
    image: require('@/assets/case studies/cabinaway.png'),
    link: 'https://cabinaway.com',
    featured: true
  },
  {
    id: 4,
    title: 'Inluminance Directory',
    client: 'Inluminance',
    service: 'Platforms',
    industry: 'Health Coaching',
    year: 2024,
    description: 'A curated directory platform for health and wellness coaches to list their services and connect with clients.',
    longDescription: 'Inluminance Directory is a vetted listing platform for health coaches, therapists, and wellness practitioners. We built the full directory infrastructure — submission forms, practitioner profiles, search and filter functionality, and a clean browsable interface for clients seeking support.',
    challenge: 'The client needed a directory that felt premium and trustworthy, not like a generic listing site. Practitioners needed simple submission tools, while visitors needed powerful search to find the right match. Managing quality control at scale was a key design consideration.',
    approach: 'We developed a WordPress-based directory with custom post types for practitioner profiles, advanced filtering by specialty, location, and modality, and a streamlined submission and approval workflow. The frontend was designed to feel curated and calm — matching the wellness brand.',
    impact: {
      metrics: [
        { label: 'Filter Categories', value: '8+' },
        { label: 'Profile Fields', value: '15+' },
        { label: 'Search Speed', value: '<1s' },
        { label: 'Submission Flow', value: 'Streamlined' }
      ]
    },
    outcomes: [
      'Built a fully functional directory with approval workflows',
      'Enabled practitioners to self-manage their profiles and listings',
      'Created powerful search and filter tools for clients finding support',
      'Delivered a premium aesthetic that matches the wellness industry'
    ],
    duration: '8 weeks',
    technologies: ['WordPress', 'GravityForms', 'Custom Post Types', 'Advanced Custom Fields'],
    image: require('@/assets/case studies/inluminance.png'),
    link: 'https://directory.inluminance.com/',
    featured: false
  },
  {
    id: 5,
    title: 'iPublic Accountants',
    client: 'iPublic Accountants',
    service: 'Web Design',
    industry: 'Finance',
    year: 2024,
    description: 'A modern, trust-building website for an Australian accounting firm targeting individuals and small businesses.',
    longDescription: 'iPublic Accountants required a website that positions them as a modern, approachable alternative to traditional accounting firms. We built a site that balances professional credibility with clear communication of their services, pricing transparency, and a strong lead generation focus.',
    challenge: 'Accounting firm websites tend to look either too corporate or too generic. iPublic wanted to stand out with a fresh, modern aesthetic while building enough trust for visitors to hand over sensitive financial matters. Converting visitors to consultations was the primary goal.',
    approach: 'We focused on clear service segmentation (individuals vs businesses), added social proof elements, and structured the site around common client questions. A prominent contact and consultation booking flow was built into every page to maximise lead capture.',
    impact: {
      metrics: [
        { label: 'Services Presented', value: '8+' },
        { label: 'Lead Forms', value: 'Every Page' },
        { label: 'Mobile Score', value: '95+' },
        { label: 'Load Time', value: '<2s' }
      ]
    },
    outcomes: [
      'Modernised brand perception and differentiated from traditional accounting firms',
      'Improved lead generation through strategically placed conversion points',
      'Clearly communicated services to both individual and business clients',
      'Delivered a fast, mobile-optimised experience for all users'
    ],
    duration: '4 weeks',
    technologies: ['WordPress', 'Elementor', 'WPForms', 'Custom CSS'],
    image: require('@/assets/case studies/ipublic accounts.png'),
    link: 'https://ipublicaccountants.com.au/',
    featured: false
  },
  {
    id: 6,
    title: 'Premier Writing Group',
    client: 'Premier Writing Group',
    service: 'Web Design',
    industry: 'Professional Services',
    year: 2024,
    description: 'A polished service website for a professional writing and content agency, designed to attract high-value clients.',
    longDescription: 'Premier Writing Group provides ghostwriting, copywriting, and content strategy services to professionals and businesses. We built a site that communicates their expertise with confidence — using strong copywriting principles on the site itself to demonstrate their craft.',
    challenge: 'A writing agency\'s website is judged more harshly than most — every word matters. The site needed to showcase range across industries, establish authority, and drive enquiries from clients with high-value projects. Generic templates would not suffice.',
    approach: 'We crafted every page with intentional copy and structure, built a portfolio showcase, and designed service pages around client outcomes rather than process. Clear pricing tiers and a low-friction inquiry form were added to reduce the barrier to engagement.',
    impact: {
      metrics: [
        { label: 'Service Pages', value: '6+' },
        { label: 'Portfolio Pieces', value: 'Yes' },
        { label: 'Conversion CTA', value: 'Every Page' },
        { label: 'Brand Authority', value: 'High' }
      ]
    },
    outcomes: [
      'Built a site that demonstrates writing expertise through its own copy and structure',
      'Established clear service tiers for different client needs and budgets',
      'Created a portfolio section to showcase range and past work',
      'Optimised inquiry flow to attract and convert high-value project enquiries'
    ],
    duration: '4 weeks',
    technologies: ['WordPress', 'Elementor', 'Custom CSS', 'WPForms'],
    image: require('@/assets/case studies/premier writing group.png'),
    link: 'https://premierwritinggroup.com/',
    featured: false
  },
  {
    id: 7,
    title: 'Melody Ripple',
    client: 'Melody Ripple',
    service: 'E-Commerce',
    industry: 'Music / Entertainment',
    year: 2024,
    description: 'An e-commerce store for music merchandise, digital downloads, and artist products with a strong brand identity.',
    longDescription: 'Melody Ripple is a music-focused e-commerce brand selling merchandise, digital content, and artist-related products. We built a vibrant, on-brand WooCommerce store that supports both physical product sales and digital downloads, with a shopping experience tailored to music fans.',
    challenge: 'Music merchandise stores need to balance strong visual identity with reliable e-commerce functionality. Melody Ripple needed a store that felt like an extension of the artist brand, supported digital and physical products, and provided a seamless checkout experience.',
    approach: 'We built a fully custom WooCommerce store with digital download support, variable product options, and a checkout flow optimised for conversion. The design language was driven by the brand aesthetic — bold, energetic, and distinctly music-focused.',
    impact: {
      metrics: [
        { label: 'Product Types', value: '2+' },
        { label: 'Digital Downloads', value: 'Yes' },
        { label: 'Payment Gateways', value: '2+' },
        { label: 'Mobile Checkout', value: 'Optimised' }
      ]
    },
    outcomes: [
      'Launched a fully operational e-commerce store for music merchandise and downloads',
      'Integrated digital product delivery for instant, automated fulfilment',
      'Created a visually compelling storefront that extends the artist brand',
      'Streamlined checkout experience to maximise purchase completion rates'
    ],
    duration: '5 weeks',
    technologies: ['WordPress', 'WooCommerce', 'Stripe', 'Custom CSS'],
    image: require('@/assets/case studies/melody ripple.png'),
    link: 'https://melodyripple.com/',
    featured: false
  },
  {
    id: 8,
    title: 'Fiume Agency',
    client: 'Fiume Agency',
    service: 'Web Design',
    industry: 'Digital Agency',
    year: 2024,
    description: 'A sophisticated agency website with bold design, interactive elements, and a strong case study presentation.',
    longDescription: 'Fiume is a digital agency that needed a site as impressive as the work they deliver for clients. We designed and developed a high-end agency website with polished interactions, a compelling case study layout, and a brand voice that positions them at the top of their field.',
    challenge: 'Agency websites are some of the hardest to get right — they must impress creative peers, convince business clients, and reflect the agency\'s capabilities without overshadowing the work. Fiume needed a site that commands attention and communicates premium positioning.',
    approach: 'We led with bold typography and minimal distraction, letting the work speak through well-structured case studies. Subtle animations and interactions were added to demonstrate technical capability without sacrificing performance. The overall feel is confident, modern, and intentional.',
    impact: {
      metrics: [
        { label: 'Case Studies', value: 'Featured' },
        { label: 'Animation', value: 'Subtle & Polished' },
        { label: 'Performance', value: '90+ Score' },
        { label: 'Brand Positioning', value: 'Premium' }
      ]
    },
    outcomes: [
      'Delivered a premium agency site that commands respect from both peers and clients',
      'Showcased case studies in a format that drives enquiries from quality prospects',
      'Implemented polished micro-interactions that demonstrate design capability',
      'Achieved high performance scores without compromising visual quality'
    ],
    duration: '6 weeks',
    technologies: ['WordPress', 'Custom Theme', 'GSAP', 'Custom CSS'],
    image: require('@/assets/case studies/fiume.png'),
    link: 'https://fiume.io',
    featured: false
  },
  {
    id: 9,
    title: 'Buzzy Agents',
    client: 'Buzzy Agents',
    service: 'Web Design',
    industry: 'Real Estate',
    year: 2024,
    description: 'A lead generation website for real estate agents, focused on vendor and buyer enquiry conversion.',
    longDescription: 'Buzzy Agents is a real estate brand requiring a high-converting website to attract property vendors and buyers. We built a site structured around lead generation — with property-specific landing pages, social proof, and clear CTAs throughout to drive enquiries to the agent team.',
    challenge: 'Real estate is a trust-driven industry where prospects compare multiple agents before committing. Buzzy Agents needed a site that builds credibility quickly, clearly communicates their local expertise, and converts visitors into leads before they click away.',
    approach: 'We built lead capture into the architecture of the site — from hero forms to sticky contact elements. Agent profiles, testimonials, and sold results were strategically placed to build trust. The design is clean and confident, reflecting a professional local agency.',
    impact: {
      metrics: [
        { label: 'Lead Forms', value: 'Multiple' },
        { label: 'Agent Profiles', value: 'Showcased' },
        { label: 'Mobile Optimised', value: '100%' },
        { label: 'Trust Signals', value: 'Throughout' }
      ]
    },
    outcomes: [
      'Built a lead generation focused site structured around vendor and buyer enquiries',
      'Showcased agent profiles and testimonials to build trust with prospects',
      'Integrated multiple contact points and forms throughout the user journey',
      'Delivered a mobile-first experience for on-the-go property seekers'
    ],
    duration: '4 weeks',
    technologies: ['WordPress', 'Elementor', 'WPForms', 'Custom CSS'],
    image: require('@/assets/case studies/buzzy agents.png'),
    link: 'https://buzzyagents.com/',
    featured: false
  },
  {
    id: 10,
    title: 'Upstate',
    client: 'Upstate',
    service: 'Web Design',
    industry: 'Lifestyle / Travel',
    year: 2024,
    description: 'An editorial-style lifestyle and travel website with a strong visual identity and content-first design.',
    longDescription: 'Upstate is a lifestyle and travel brand with a distinct editorial voice. We built a content-driven website that prioritises beautiful photography, long-form articles, and curated destination guides — creating an experience that keeps readers engaged and coming back.',
    challenge: 'Lifestyle and travel brands live or die by their visual presentation and content discoverability. Upstate needed a site that felt like a premium publication, made content easy to explore, and translated seamlessly across devices for readers browsing on the go.',
    approach: 'We built an editorial-first WordPress site with a clean grid layout, flexible article templates, and strong typographic hierarchy. Content categorisation and internal linking were built into the architecture to maximise time-on-site and drive discovery of related content.',
    impact: {
      metrics: [
        { label: 'Content First', value: 'Yes' },
        { label: 'Article Templates', value: 'Flexible' },
        { label: 'Mobile Reading', value: 'Optimised' },
        { label: 'Page Speed', value: '90+ Score' }
      ]
    },
    outcomes: [
      'Created an immersive editorial experience that showcases travel content beautifully',
      'Built flexible article and guide templates that scale with growing content',
      'Optimised for mobile reading with clean typography and fast load times',
      'Structured content discovery to increase pages per session and return visits'
    ],
    duration: '5 weeks',
    technologies: ['WordPress', 'Custom Theme', 'ACF', 'Custom CSS'],
    image: require('@/assets/case studies/upstate.png'),
    link: 'https://www.upstate.to/',
    featured: false
  }
];
